/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useState } from 'react';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { TextInput, useWindowDimensions, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { NumericTextInput } from '../../../shared/ui/NumericTextInput';
import { showSuccess } from '../../../shared/ui/appToast';
import { createBrand, setBrandActive, updateBrand, countBrandsLocal, listBrandsLocalPaged } from '../../infra/brandsRepo'
import { BrandNameDuplicateError } from '../../domain/catalogErrors';
import type { BrandFilterState, BrandRecord } from '../../domain/catalogBrandTypes';
import { styles } from './CatalogMastersScreen.styles';
import { BrandMasterCard } from './BrandMasterCard';
import { BrandMasterTable } from './BrandMasterTable';
import { MasterFormModal } from './MasterFormModal';
import { MasterFormField } from './MasterFormField';
import { MastersSectionHeader } from './MastersSectionHeader';
import { useMastersPagination } from './useMastersPagination';
import { MastersListFiltersPanel } from '../../../shared/filters/MastersListFiltersPanel';
import {
  CatalogMasterConfirmModal,
  type CatalogMasterConfirmSpec,
} from './CatalogMasterConfirmModal';
import { MasterDetailModal } from './MasterDetailModal';
import { MastersSectionListShell } from './MastersSectionListShell';
import {
  MASTERS_WIDE_LAYOUT_BREAKPOINT,
  mastersFilterOptions,
  type MasterSectionStats,
} from './mastersSectionShared';

const FILTER_OPTIONS = mastersFilterOptions({ active: 'Activas', inactive: 'Inactivas', all: 'Todas' });
const LIST_NOUNS = { singular: 'marca', plural: 'marcas' };

export type BrandsMasterSectionProps = {
  canEdit: boolean;
  stats?: MasterSectionStats | null;
  onOpenCsv?: () => void;
  onRecordsChanged?: () => void;
  onBindReload?: (reload: () => void) => void;
};

type DraftState = {
  mode: 'create' | 'edit';
  id?: string;
  name: string;
  code: string;
  website: string;
  defaultMarkupPercent: string;
};

/** Campos del draft con validación inline. */
type BrandFieldKey = 'name' | 'website' | 'defaultMarkupPercent';

const emptyDraft = (): DraftState => ({ mode: 'create', name: '', code: '', website: '', defaultMarkupPercent: '' });

export const BrandsMasterSection = ({
  canEdit,
  stats,
  onOpenCsv,
  onRecordsChanged,
  onBindReload,
}: BrandsMasterSectionProps) => {
  const { width } = useWindowDimensions();
  const isWide = width >= MASTERS_WIDE_LAYOUT_BREAKPOINT;
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<DraftState>(() => emptyDraft());
  const [filterState, setFilterState] = useState<BrandFilterState>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingMode, setEditingMode] = useState<boolean>(false);
  const [masterConfirmSpec, setMasterConfirmSpec] = useState<CatalogMasterConfirmSpec | null>(null);
  const [detailBrand, setDetailBrand] = useState<BrandRecord | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<BrandFieldKey, string>>>({});

  const updateDraft = useCallback(
    <K extends keyof DraftState>(key: K, value: DraftState[K]) => {
      setDraft((prev) => ({ ...prev, [key]: value }));
      setFieldErrors((prev) =>
        prev[key as BrandFieldKey] ? { ...prev, [key as BrandFieldKey]: undefined } : prev,
      );
    },
    [],
  );

  const {
    items: brands,
    pageIndex,
    pageSize,
    totalCount,
    hasMore,
    loading: paginationLoading,
    error: paginationError,
    setPageSize,
    nextPage,
    previousPage,
    reload,
  } = useMastersPagination<BrandRecord>({
    fetchCount: countBrandsLocal,
    fetchPage: listBrandsLocalPaged,
    filterState,
    searchQuery,
    storageKey: 'brands_page_size',
  });

  useEffect(() => {
    onBindReload?.(reload);
    return () => onBindReload?.(() => {});
  }, [reload, onBindReload]);

  const onSave = useCallback(async () => {
    // Validación inline: el error queda al lado del campo, no solo en un toast.
    const nextErrors: Partial<Record<BrandFieldKey, string>> = {};

    const name = draft.name.trim();
    if (!name) {
      nextErrors.name = 'Ingresá un nombre para la marca.';
    }
    const code = draft.code.trim() || null;
    const website = draft.website.trim() || null;

    let defaultMarkupPercent: number | null = null;
    if (draft.defaultMarkupPercent.trim()) {
      const num = parseDecimalInput(draft.defaultMarkupPercent.trim(), { min: 0, max: 100 });
      if (num === null) {
        nextErrors.defaultMarkupPercent = 'Ingresá un número entre 0 y 100 (ej. 10 o 10,5).';
      } else {
        defaultMarkupPercent = num;
      }
    }

    if (website && !website.match(/^https?:\/\/.+/i)) {
      nextErrors.website = 'El sitio web debe comenzar con http:// o https://';
    }

    setFieldErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setSaving(true);
    try {
      if (draft.mode === 'edit' && draft.id) {
        await updateBrand({ id: draft.id, name, code, website, defaultMarkupPercent });
        showSuccess('Marca actualizada', 'Los cambios se guardaron.');
      } else {
        await createBrand({ name, code, website, defaultMarkupPercent });
        showSuccess('Marca creada', 'Ya podés asignarla a productos.');
      }
      setDraft(emptyDraft());
      setFieldErrors({});
      setEditingMode(false);
      reload();
      onRecordsChanged?.();
    } catch (error: unknown) {
      if (error instanceof BrandNameDuplicateError) {
        // Duplicado: error inline en el campo Nombre, el modal queda abierto.
        setFieldErrors((prev) => ({ ...prev, name: error.message }));
        return;
      }
      presentUnexpectedFailure({ module: 'catalog', op: 'brand.save', error, userMessage: 'No se pudo guardar la marca.' });
    } finally {
      setSaving(false);
    }
  }, [draft, reload, onRecordsChanged, setEditingMode]);

  const onEdit = useCallback((brand: BrandRecord) => {
    setDraft({
      mode: 'edit',
      id: brand.id,
      name: brand.name,
      code: brand.code ?? '',
      website: brand.website ?? '',
      defaultMarkupPercent: brand.defaultMarkupPercent?.toString() ?? '',
    });
    setFieldErrors({});
    setEditingMode(true);
  }, []);

  const onCancelEdit = useCallback(() => {
    setDraft(emptyDraft());
    setFieldErrors({});
    setEditingMode(false);
  }, []);

  const onStartCreate = useCallback(() => {
    setDraft(emptyDraft());
    setFieldErrors({});
    setEditingMode(true);
  }, []);

  const applyBrandActive = useCallback(
    async (brand: BrandRecord, nextActive: boolean) => {
      setSaving(true);
      try {
        await setBrandActive({ id: brand.id, isActive: nextActive });
        reload();
        onRecordsChanged?.();
        showSuccess(
          nextActive ? 'Marca activada' : 'Marca desactivada',
          nextActive ? 'Volvió a aparecer en selectores.' : 'Ya no aparece en selectores.'
        );
      } catch (error: any) {
        presentUnexpectedFailure({ module: 'catalog', op: 'no-pudimos-cambiar-el-estado', error: error, title: 'No pudimos cambiar el estado', userMessage: 'Ocurrió un error' });
      } finally {
        setSaving(false);
      }
    },
    [reload, onRecordsChanged]
  );

  const onToggleActive = useCallback(
    (brand: BrandRecord) => {
      if (brand.isActive) {
        setMasterConfirmSpec({
          kind: 'confirm',
          title: 'Desactivar marca',
          entityName: brand.name,
          message: 'Deja de aparecer en los selectores. La podés volver a mostrar desde Inactivas.',
          confirmLabel: 'Desactivar',
          iconName: 'eye-off-outline',
          onConfirm: async () => {
            setMasterConfirmSpec(null);
            await applyBrandActive(brand, false);
          },
        });
        return;
      }
      void applyBrandActive(brand, true);
    },
    [applyBrandActive]
  );

  const onOpenDetail = useCallback((brand: BrandRecord) => {
    setDetailBrand(brand);
  }, []);

  const onCloseDetail = useCallback(() => {
    setDetailBrand(null);
  }, []);

  return (
    <View style={{ gap: spacing.lg }}>
      <MastersListFiltersPanel
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        searchLabel="Buscar marcas"
        searchPlaceholder="Buscar por nombre o código"
        stateOptions={FILTER_OPTIONS.map((opt) => ({ value: opt.id, label: opt.label }))}
        stateCounts={stats ?? undefined}
        filterState={filterState}
        onFilterStateChange={setFilterState}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        layoutWide={isWide}
        trailingActions={
          <MastersSectionHeader
            canEdit={canEdit}
            editingMode={editingMode}
            csvLabel="CSV"
            createLabel="Agregar marca"
            onOpenCsv={onOpenCsv}
            onCreate={onStartCreate}
          />
        }
      />

      <MastersSectionListShell
        loading={paginationLoading}
        error={paginationError}
        itemCount={brands.length}
        totalCount={totalCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        hasMore={hasMore}
        listNouns={LIST_NOUNS}
        loadingLabel="Cargando marcas…"
        emptyIcon="pricetags-outline"
        emptyTitle="Sin marcas"
        emptyErrorTitle="No pudimos cargar marcas"
        emptyMessage="Creá tu primera marca para agrupar productos."
        canEdit={canEdit}
        createLabel="Agregar"
        onCreate={onStartCreate}
        onRetry={reload}
        isWide={isWide}
        onPreviousPage={previousPage}
        onNextPage={nextPage}
        onPageSizeChange={setPageSize}
        renderWide={() => <BrandMasterTable brands={brands} onOpenDetail={onOpenDetail} />}
        renderMobile={() =>
          brands.map((brand) => (
            <BrandMasterCard key={brand.id} brand={brand} onPress={() => onOpenDetail(brand)} />
          ))
        }
      />

      <CatalogMasterConfirmModal
        spec={masterConfirmSpec}
        onClose={() => {
          if (!saving) setMasterConfirmSpec(null);
        }}
      />

      <MasterFormModal
        visible={canEdit && editingMode}
        mode={draft.mode}
        entityKicker="Marca"
        entityName={draft.name}
        headerTitle={draft.mode === 'edit' ? 'Editar marca' : 'Nueva marca'}
        saving={saving}
        saveLabel={draft.mode === 'edit' ? 'Guardar' : 'Crear marca'}
        onSave={onSave}
        onClose={onCancelEdit}
      >
        <MasterFormField label="Nombre" error={fieldErrors.name}>
        <TextInput
          style={[styles.formInput, fieldErrors.name ? styles.formInputError : null]}
          value={draft.name}
          onChangeText={(value) => updateDraft('name', value)}
          placeholder="Ej: Coca-Cola"
          placeholderTextColor={ticketColors.ink3}
          autoCapitalize="words"
          editable={!saving}
          accessibilityLabel="Nombre de la marca"
        />
        </MasterFormField>
        <MasterFormField label="Código interno">
        <TextInput
          style={styles.formInput}
          value={draft.code}
          onChangeText={(value) => updateDraft('code', value)}
          placeholder="Opcional"
          placeholderTextColor={ticketColors.ink3}
          autoCapitalize="characters"
          editable={!saving}
          accessibilityLabel="Código interno de la marca"
        />
        </MasterFormField>
        <MasterFormField label="Sitio web" error={fieldErrors.website}>
        <TextInput
          style={[styles.formInput, fieldErrors.website ? styles.formInputError : null]}
          value={draft.website}
          onChangeText={(value) => updateDraft('website', value)}
          placeholder="https://marca.com"
          placeholderTextColor={ticketColors.ink3}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="url"
          editable={!saving}
          accessibilityLabel="Sitio web de la marca"
        />
        </MasterFormField>
        <MasterFormField label="Margen % sugerido" error={fieldErrors.defaultMarkupPercent}>
        <NumericTextInput
          kind="percent"
          style={[styles.formInput, fieldErrors.defaultMarkupPercent ? styles.formInputError : null]}
          value={draft.defaultMarkupPercent}
          onChangeText={(value) => updateDraft('defaultMarkupPercent', value)}
          placeholder="Ej: 30"
          placeholderTextColor={ticketColors.ink3}
          editable={!saving}
          accessibilityLabel="Margen porcentual sugerido"
        />
        </MasterFormField>
      </MasterFormModal>

      <MasterDetailModal
        visible={!!detailBrand}
        entityType="brand"
        entity={detailBrand!}
        canEdit={canEdit}
        onClose={onCloseDetail}
        onEdit={() => {
          if (detailBrand) {
            onCloseDetail();
            onEdit(detailBrand);
          }
        }}
        onToggleActive={() => {
          if (detailBrand) {
            onCloseDetail();
            onToggleActive(detailBrand);
          }
        }}
      />
    </View>
  );
};
